if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.syncTagDiscountPrice(this.getSelectedVariant(this));
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          this.syncTagDiscountPrice(variant);
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      // Spiegelt snippets/price.liquid: ein reiner Zahlen-Tag 1-99 ist ein
      // Prozentrabatt, ein Tag "eur<Betrag>" (eur2.5, eur5.0 ...) ein fester
      // Eurobetrag. Beide werden NIE kombiniert - es gewinnt der Rabatt, der
      // zum niedrigeren Endpreis fuehrt, genau wie bei den Rabattcodes.
      // Der Prozentrabatt wird - wie bei Shopify - auf den Rabattbetrag gerundet.
      syncTagDiscountPrice(variant) {
        const priceContainer = this.querySelector(`#price-${this.dataset.section}`);
        const price = priceContainer?.querySelector('.price');
        const saleContainer = price?.querySelector('.price__sale');
        const variantPrice = parseInt(variant?.price, 10);

        if (!price || !saleContainer || Number.isNaN(variantPrice)) return;

        const discountPercent = parseInt(
          price.dataset.tagDiscountPercent || this.dataset.tagDiscountPercent || '0',
          10
        );
        const discountAmount = parseInt(
          price.dataset.tagDiscountAmount || this.dataset.tagDiscountAmount || '0',
          10
        );

        let discountedPrice = null;
        let discountKind = '';

        if (discountPercent > 0) {
          discountedPrice = variantPrice - Math.round((variantPrice * discountPercent) / 100);
          discountKind = 'percent';
        }

        if (discountAmount > 0) {
          const amountPrice = Math.max(0, variantPrice - discountAmount);
          if (discountedPrice === null || amountPrice < discountedPrice) {
            discountedPrice = amountPrice;
            discountKind = 'amount';
          }
        }

        if (discountedPrice === null) {
          price.classList.remove('price--tag-sale');
          this.syncTagDiscountUnitPrice(priceContainer, variantPrice, null);
          return;
        }

        const currencyCode = price.dataset.currencyCode || this.dataset.currencyCode;
        const regularFormatted = this.formatMoney(variantPrice, currencyCode);
        const discountedFormatted = this.formatMoney(discountedPrice, currencyCode);
        const savingsLabel =
          discountKind === 'amount'
            ? `-${this.formatMoney(discountAmount, currencyCode)}`
            : `-${discountPercent}%`;

        price.classList.add('price--tag-sale');
        price.dataset.tagDiscountPercent = discountPercent.toString();
        price.dataset.tagDiscountAmount = discountAmount.toString();
        saleContainer.innerHTML = `
          <span class="price__sale-row">
            <span class="price-item price-item--sale price-item--last">${discountedFormatted}</span>
            <s class="price-item price-item--regular price-item--compare">${regularFormatted}</s>
            <span class="price-savings-badge">${savingsLabel}</span>
          </span>
        `;

        this.syncTagDiscountUnitPrice(priceContainer, variantPrice, discountedPrice);
      }

      // Die Grundpreis-Pille ("x,xx EUR pro Packung") wird serverseitig in
      // sections/main-product.liquid gerendert. Hier wird sie nach einem
      // Variantenwechsel gegengerechnet und - falls das Liquid den Sale-Span
      // nicht gerendert hat - nachgezogen.
      syncTagDiscountUnitPrice(priceContainer, variantPrice, discountedPrice) {
        const unitPrice = priceContainer?.querySelector('.pdp-unit-price');
        if (!unitPrice) return;

        const price = priceContainer.querySelector('.price');
        const unitsPerPack = parseInt(price?.dataset.unitsPerPack || '0', 10);
        const regularSpan = unitPrice.querySelector('.pdp-unit-price__regular');

        if (discountedPrice === null || !unitsPerPack || unitsPerPack < 2 || !regularSpan) {
          unitPrice.classList.remove('pdp-unit-price--has-tag-discount');
          unitPrice.querySelector('.pdp-unit-price__sale')?.remove();
          return;
        }

        const currencyCode = price.dataset.currencyCode || this.dataset.currencyCode;
        const discountedPerUnit = Math.floor(discountedPrice / unitsPerPack);

        // Suffix (" pro Packung") aus dem serverseitig gerenderten Span
        // uebernehmen, damit Label und Uebersetzung erhalten bleiben.
        const regularText = regularSpan.textContent || '';
        const proIndex = regularText.indexOf(' pro ');
        const suffix = proIndex >= 0 ? regularText.slice(proIndex) : '';

        let saleSpan = unitPrice.querySelector('.pdp-unit-price__sale');
        if (!saleSpan) {
          saleSpan = document.createElement('span');
          saleSpan.className = 'pdp-unit-price__sale';
          unitPrice.appendChild(saleSpan);
        }
        saleSpan.textContent = `${this.formatMoney(discountedPerUnit, currencyCode)}${suffix}`;
        unitPrice.classList.add('pdp-unit-price--has-tag-discount');
      }

      formatMoney(cents, currencyCode) {
        const locale = document.documentElement.lang || undefined;
        const currency = currencyCode || window?.Shopify?.currency?.active || 'EUR';

        try {
          return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency,
          }).format(cents / 100);
        } catch (error) {
          return (cents / 100).toFixed(2);
        }
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        // Gleicht eine Medienliste (Hauptslider ODER Thumbnails) gegen das frisch
        // gerenderte HTML ab: fehlende Eintraege einfuegen, ueberzaehlige entfernen,
        // danach in die Reihenfolge des Ziels bringen.
        //
        // Dawn synchronisierte nur den Hauptslider. Solange serverseitig nie Medien
        // weggefiltert werden, faellt das nicht auf. Sobald die Galerie pro Variante
        // aber nur eine Teilmenge rendert (Merch-Farbgruppen), bleiben die Thumbnails
        // der alten Variante stehen und setActiveMedia findet ihr Thumbnail nicht mehr.
        const syncGalleryList = (sourceList, destinationList, key) => {
          if (!sourceList || !destinationList) return;

          const selector = `li[${key}]`;
          const getKey = (el) => el.getAttribute(key);

          const sourceItems = Array.from(sourceList.querySelectorAll(selector));
          const destinationItems = Array.from(destinationList.querySelectorAll(selector));
          const sourceSet = new Set(sourceItems.map(getKey));
          const destinationSet = new Set(destinationItems.map(getKey));

          // fehlende Medien aus dem neuen HTML uebernehmen
          for (let i = destinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(getKey(destinationItems[i]))) {
              sourceList.prepend(destinationItems[i]);
            }
          }

          // Medien entfernen, die es fuer die neue Variante nicht mehr gibt
          sourceItems.forEach((item) => {
            if (!destinationSet.has(getKey(item))) item.remove();
          });

          // in die Reihenfolge des neuen HTML bringen
          destinationItems.forEach((destinationItem) => {
            const match = sourceList.querySelector(`li[${key}="${getKey(destinationItem)}"]`);
            if (match) sourceList.appendChild(match);
          });
        };

        if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);

        syncGalleryList(
          this.querySelector('media-gallery [id^="Slider-Gallery"]'),
          html.querySelector('media-gallery [id^="Slider-Gallery"]'),
          'data-media-id'
        );

        syncGalleryList(
          this.querySelector('media-gallery [id^="Slider-Thumbnails"]'),
          html.querySelector('media-gallery [id^="Slider-Thumbnails"]'),
          'data-target'
        );

        const gallery = this.querySelector('media-gallery');

        // Neu eingefuegte Thumbnail-Buttons brauchen ihren Click-Handler
        gallery?.bindThumbnails?.();

        // set featured media as active in the media gallery
        gallery?.setActiveMedia?.(`${this.dataset.section}-${variantFeaturedMediaId}`, true);

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}